import React from 'react';
import PhotoGallery from '../../../../../Components/PhotoGallery';
import PhotoWithMarker from '../../../../../Components/PhotoWithMarker';
import BlueprintWithMarkers from '../../../../../Components/BlueprintWithMarkers';
import { PhotoLocation, BlueprintLocation, Building, PhotoModel } from '../../../../../types/protocols';
import { findBlueprintImage } from '../../../../../Helpers/blueprintLookup';
import { primaryPhotoLocation } from '../../../../../Helpers/photoLocations';

interface ComponentImagesProps {
    photos: Record<string, PhotoModel>;
    buildingKey: string;
    building?: Building | null;
    photoLocations?: Record<string, PhotoLocation> | null;
    blueprintLocations?: Record<string, BlueprintLocation>;
    mapLocations?: Record<string, BlueprintLocation>;
}

/**
 * Component images display using PhotoGallery
 * Also displays the primary photo location (position in photo) if available
 * Shows blueprint and map locations with markers (aligned right)
 */
export const ComponentImages: React.FC<ComponentImagesProps> = ({
    photos,
    buildingKey,
    building,
    photoLocations,
    blueprintLocations,
    mapLocations
}) => {
    const hasPhotos = photos && Object.keys(photos).length > 0;
    const photoLocation = primaryPhotoLocation({ photoLocations: photoLocations ?? undefined }, building);
    const hasPhotoLocation = !!photoLocation;
    const hasBlueprintLocations = blueprintLocations && Object.keys(blueprintLocations).length > 0;
    const hasMapLocations = mapLocations && Object.keys(mapLocations).length > 0;
    const hasAnyLocation = hasPhotoLocation || hasBlueprintLocations || hasMapLocations;

    if (!hasPhotos && !hasAnyLocation) {
        return null;
    }

    // Get blueprint data for BlueprintWithMarkers
    const blueprintItems: Array<{
        key: string;
        location: BlueprintLocation;
        image: PhotoModel;
        label: string;
    }> = [];

    if (hasBlueprintLocations && building?.model?.structure) {
        Object.entries(blueprintLocations!).forEach(([blueprintKey, location]) => {
            // A level's drawing or the roof drawing
            const found = findBlueprintImage(building.model.structure, blueprintKey);
            if (found) {
                blueprintItems.push({
                    key: blueprintKey,
                    location,
                    image: found.image,
                    label: found.owner.kind === 'roofPlan' ? 'Takplan' : 'Ritning'
                });
            }
        });
    }

    // Get map data for BlueprintWithMarkers
    const mapItems: Array<{
        key: string;
        location: BlueprintLocation;
        image: PhotoModel;
        label: string;
    }> = [];

    if (hasMapLocations && building?.model?.mapImages) {
        Object.entries(mapLocations!).forEach(([mapKey, location]) => {
            const mapImage = building.model.mapImages?.[mapKey];
            if (mapImage && typeof mapImage !== 'string') {
                mapItems.push({
                    key: mapKey,
                    location,
                    image: mapImage,
                    label: 'Karta'
                });
            }
        });
    }

    const hasLocationItems = hasPhotoLocation || blueprintItems.length > 0 || mapItems.length > 0;

    return (
        <div className="component-images-row">
            <div className={`images-and-locations ${hasLocationItems ? 'has-locations' : ''}`}>
                {/* Photos column - takes remaining space */}
                {hasPhotos && (
                    <div className="photos-column">
                        <PhotoGallery
                            photos={photos}
                            buildingKey={buildingKey}
                            thumbnailSize={90}
                        />
                    </div>
                )}

                {/* Locations column - fixed width based on content */}
                {hasLocationItems && (
                    <div className="locations-column">
                        {/* Photo Location - using PhotoWithMarker */}
                        {hasPhotoLocation && (
                            <div className="location-item">
                                <div className="blueprint-preview-container">
                                    <PhotoWithMarker
                                        photoLocation={photoLocation}
                                        title=""
                                    />
                                </div>
                                <span className="location-label">Position</span>
                            </div>
                        )}

                        {/* Blueprint Locations - using BlueprintWithMarkers */}
                        {building && blueprintItems.map(({ key, location, image, label }) => (
                            <div key={`blueprint-${key}`} className="location-item">
                                <div className="blueprint-preview-container">
                                    <BlueprintWithMarkers
                                        blueprint={image}
                                        currentBlueprintKey={key}
                                        building={building}
                                        pointMarker={location}
                                        title=""
                                        disableZoom={true}
                                    />
                                </div>
                                <span className="location-label">{label}</span>
                            </div>
                        ))}

                        {/* Map Locations - using BlueprintWithMarkers */}
                        {building && mapItems.map(({ key, location, image, label }) => (
                            <div key={`map-${key}`} className="location-item">
                                <div className="blueprint-preview-container">
                                    <BlueprintWithMarkers
                                        blueprint={image}
                                        currentBlueprintKey={key}
                                        building={building}
                                        pointMarker={location}
                                        title=""
                                        disableZoom={true}
                                    />
                                </div>
                                <span className="location-label">{label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
